import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ClipboardList,
  Receipt,
  Kanban,
  Clock,
  CreditCard,
} from "lucide-react";
import Button from "../components/common/Button";

const wrap = {
  maxWidth: "1260px",
  margin: "0 auto",
  padding: "34px 16px 56px",
};

const panel = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.035))",
  border: "1px solid var(--hairline)",
  borderRadius: "18px",
};

const leadRows = [
  { title: "Bathroom reno", suburb: "New Farm", tag: "$4.8k", tone: "green" },
  { title: "Deck repair", suburb: "Wynnum", tag: "Quote", tone: "orange" },
  { title: "Kitchen splashback", suburb: "Paddington", tag: "Paid", tone: "green" },
];

const pipelineColumns = [
  {
    title: "New",
    items: [
      { title: "Leaking shower", detail: "Fortitude Valley" },
      { title: "Retaining wall", detail: "Carindale" },
    ],
  },
  {
    title: "Quoted",
    items: [
      { title: "Office fitout", detail: "$6,420 sent" },
      { title: "Fence repair", detail: "Follow up today" },
    ],
  },
  {
    title: "Accepted",
    items: [{ title: "Patio stairs", detail: "Deposit paid" }],
  },
  {
    title: "Booked",
    items: [{ title: "Roof patch", detail: "Tomorrow 8am" }],
  },
  {
    title: "Done",
    items: [{ title: "Hot water job", detail: "Invoice paid" }],
  },
];

const features = [
  {
    icon: ClipboardList,
    title: "Quote fast",
    body: "Build itemised quotes from saved line items while the job is fresh.",
  },
  {
    icon: Receipt,
    title: "Chase less",
    body: "Accepted jobs and invoice status stay visible until they are sorted.",
  },
  {
    icon: Kanban,
    title: "Stay organised",
    body: "Every lead sits in the right stage so nothing slips off the radar.",
  },
];

function Pill({ children, tone = "orange" }) {
  const isGreen = tone === "green";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 9px",
        borderRadius: "999px",
        fontSize: "0.68rem",
        fontWeight: 850,
        whiteSpace: "nowrap",
        color: isGreen ? "#87F3B8" : "var(--primary)",
        background: isGreen ? "rgba(72, 219, 141, 0.14)" : "var(--primary-soft)",
      }}
    >
      {children}
    </span>
  );
}

function PhonePreview() {
  return (
    <motion.aside
      initial={{ opacity: 0, y: 18, rotate: -1 }}
      animate={{ opacity: 1, y: 0, rotate: -2 }}
      transition={{ duration: 0.5, delay: 0.08 }}
      style={{
        width: "min(390px, 100%)",
        justifySelf: "center",
        borderRadius: "34px",
        border: "1px solid rgba(255,255,255,0.16)",
        background: "#101011",
        boxShadow: "0 34px 92px rgba(0,0,0,0.58)",
        padding: "14px",
      }}
    >
      <div
        style={{
          borderRadius: "24px",
          background: "linear-gradient(180deg, #2b2b2f, #121214)",
          padding: "17px",
          color: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            marginBottom: "12px",
          }}
        >
          <strong>Today</strong>
          <Pill>3 new</Pill>
        </div>

        {leadRows.map((row) => (
          <div
            key={row.title}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              marginTop: "10px",
              borderRadius: "15px",
              padding: "14px",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div>
              <strong style={{ display: "block" }}>{row.title}</strong>
              <span style={{ display: "block", color: "rgba(255,255,255,0.68)", fontSize: "0.76rem", marginTop: "4px" }}>
                {row.suburb}
              </span>
            </div>
            <Pill tone={row.tone}>{row.tag}</Pill>
          </div>
        ))}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "10px",
            marginTop: "12px",
          }}
        >
          {[
            ["12", "Active jobs"],
            ["$8k", "Quoted"],
            ["4", "To chase"],
          ].map(([value, label]) => (
            <div key={label} style={{ ...panel, padding: "14px" }}>
              <strong style={{ display: "block", fontSize: "1.45rem" }}>{value}</strong>
              <span style={{ display: "block", color: "rgba(255,255,255,0.66)", fontSize: "0.74rem", marginTop: "4px" }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.aside>
  );
}

function HeroSection({ setCurrentView }) {
  return (
    <motion.section
      className="landing-responsive-hero"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 0.95fr) minmax(340px, 0.7fr)",
        gap: "28px",
        alignItems: "center",
        minHeight: "610px",
        padding: "40px 0 54px",
      }}
    >
      <div>
        <span
          style={{
            display: "inline-flex",
            width: "fit-content",
            padding: "7px 12px",
            borderRadius: "999px",
            background: "var(--primary-soft)",
            border: "1px solid rgba(255, 138, 0, 0.28)",
            color: "var(--primary)",
            fontSize: "0.72rem",
            fontWeight: 850,
            textTransform: "uppercase",
          }}
        >
          Built for Australian tradies
        </span>
        <h1
          style={{
            fontSize: "clamp(3.15rem, 7vw, 5.75rem)",
            lineHeight: 0.92,
            letterSpacing: 0,
            margin: "18px 0",
            maxWidth: "780px",
            color: "var(--text-1, var(--text))",
          }}
        >
          Quote from site. Keep the job moving.
        </h1>
        <p
          style={{
            color: "var(--text-2)",
            fontSize: "1.18rem",
            lineHeight: 1.5,
            maxWidth: "620px",
            margin: 0,
          }}
        >
          Send clean quotes, collect deposits, and see every lead without
          digging through texts, spreadsheets, and missed calls.
        </p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "28px" }}>
          <Button onClick={() => setCurrentView("tradie")}>
            Start free <ArrowRight size={16} />
          </Button>
          <Button secondary onClick={() => setCurrentView("tradie")}>
            Tradie login
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            gap: "18px",
            flexWrap: "wrap",
            marginTop: "22px",
            color: "var(--text-3)",
            fontSize: "0.82rem",
            fontWeight: 700,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Clock size={14} /> Set up in 5 minutes
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <CreditCard size={14} /> Free during beta
          </span>
        </div>
      </div>
      <PhonePreview />
    </motion.section>
  );
}

function PipelinePreview() {
  return (
    <section style={{ marginTop: "10px" }}>
      <div
        className="landing-responsive-pipeline-head"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 0.85fr) minmax(260px, 0.45fr)",
          gap: "24px",
          alignItems: "end",
          margin: "0 0 18px",
        }}
      >
        <h2
          style={{
            fontSize: "clamp(2rem, 4vw, 3.25rem)",
            lineHeight: 1,
            letterSpacing: 0,
            margin: 0,
            maxWidth: "650px",
            color: "var(--text-1, var(--text))",
          }}
        >
          Your jobs in one straight line.
        </h2>
        <p style={{ color: "var(--text-2)", lineHeight: 1.5, margin: 0 }}>
          New leads, quotes, booked work, invoices, and paid jobs all move
          through one board.
        </p>
      </div>

      <div
        className="landing-responsive-pipeline"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: "12px",
        }}
      >
        {pipelineColumns.map((column, index) => (
          <motion.div
            key={column.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            style={{ ...panel, minHeight: "250px", padding: "13px" }}
          >
            <h3
              style={{
                color: "var(--text-3)",
                fontSize: "0.74rem",
                textTransform: "uppercase",
                margin: "0 0 11px",
                letterSpacing: 0,
              }}
            >
              {column.title}
            </h3>
            {column.items.map((item) => (
              <div
                key={item.title}
                style={{
                  padding: "12px",
                  borderRadius: "13px",
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  marginBottom: "10px",
                  minHeight: "68px",
                }}
              >
                <strong style={{ display: "block", fontSize: "0.82rem" }}>{item.title}</strong>
                <span style={{ display: "block", color: "var(--text-2)", fontSize: "0.76rem", marginTop: "4px" }}>
                  {item.detail}
                </span>
              </div>
            ))}
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function FeatureGrid() {
  return (
    <section
      className="landing-responsive-features"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: "14px",
        marginTop: "24px",
      }}
    >
      {features.map(({ icon: Icon, title, body }) => (
        <div key={title} style={{ ...panel, padding: "22px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "var(--primary-soft)",
              color: "var(--primary)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "14px",
            }}
          >
            <Icon size={18} />
          </div>
          <h3 style={{ margin: "0 0 8px", fontSize: "1.08rem", letterSpacing: 0 }}>
            {title}
          </h3>
          <p style={{ color: "var(--text-2)", lineHeight: 1.5, margin: 0, fontSize: "0.9rem" }}>
            {body}
          </p>
        </div>
      ))}
    </section>
  );
}

function LandingFooter() {
  return (
    <footer
      style={{
        borderTop: "1px solid var(--hairline)",
        marginTop: "56px",
        padding: "24px 0 0",
        textAlign: "center",
        color: "var(--text-3)",
        fontSize: "0.78rem",
      }}
    >
      Remote Quote Pro {new Date().getFullYear()} | Built for AU tradies |{" "}
      <a
        href="mailto:hello@remotequote.io"
        style={{ color: "inherit", textDecoration: "underline" }}
      >
        hello@remotequote.io
      </a>
    </footer>
  );
}

export default function Landing({ setCurrentView }) {
  return (
    <div style={wrap}>
      <style>
        {`
          @media (max-width: 1000px) {
            main .landing-responsive-hero {
              grid-template-columns: 1fr !important;
              min-height: auto !important;
            }
          }
          @media (max-width: 900px) {
            main .landing-responsive-pipeline-head {
              grid-template-columns: 1fr !important;
            }
            main .landing-responsive-pipeline {
              grid-template-columns: 1fr !important;
            }
            main .landing-responsive-features {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
      <div className="landing-responsive-scope">
        <HeroSection setCurrentView={setCurrentView} />
        <PipelinePreview />
        <FeatureGrid />
        <LandingFooter />
      </div>
    </div>
  );
}
